import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FormFieldType, type FormAnswer, type FormField } from '@troia-v3/types';

import {
  Breadcrumb,
  Button,
  CardActionsMenu,
  ListPageLoading,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PageToolbar,
  StatusPill,
  Textarea,
  useEscapeKey,
} from '@/shared/components/ui';
import type { CardAction, StatusPillVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';

import { useChecklist, useReopenChecklist, useReviewChecklist } from '../hooks/useChecklists';
import { CHECKLIST_STATUS_META, formatDueRelative } from './ChecklistsPage';

/** Badge de conformidade — 3 valores de `CONFORMITY_VALUES` (@troia-v3/types). */
const CONFORMITY_META: Record<string, { label: string; variant: StatusPillVariant }> = {
  conforme: { label: 'Conforme', variant: 'success' },
  nao_conforme: { label: 'Não conforme', variant: 'error' },
  na: { label: 'N/A', variant: 'neutral' },
};

/** Texto plain para tipos de campo sem renderização dedicada (spec: "texto/número plain"). */
const formatPlainAnswer = (value: FormAnswer['value'] | undefined): string => {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
};

/**
 * ChecklistReviewPage — render read-only das `answers` sobre `templateSnapshot.fields`
 * (pareado por `fieldId`, ordem do snapshot — invariante "snapshot congelado" do
 * `ChecklistsService`, ver `@DOCS/modules/DISTRIBUTION.md`/backend `service.ts`).
 *
 * `unitName`/`assigneeName` não vêm no payload (mesmo motivo documentado em
 * `ChecklistsPage`) — resolvidos client-side pelos mesmos hooks dropdown.
 */
export const ChecklistReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { state } = useAuth();
  const { hasModulePermission } = usePermissions();
  const canReview = hasModulePermission('checklists', 'update');

  const { data: checklist, isLoading, error } = useChecklist(id);
  const { data: unitOptions } = useUnitsDropdown();
  const { data: userOptions } = useUsersDropdown();
  const reviewMutation = useReviewChecklist();
  const reopenMutation = useReopenChecklist();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectError, setRejectError] = useState<string | undefined>();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEscapeKey(() => setLightboxUrl(null), lightboxUrl !== null);

  const unitMap = useMemo(() => new Map((unitOptions ?? []).map((u) => [u.value, u.label])), [unitOptions]);
  const userMap = useMemo(() => new Map((userOptions ?? []).map((u) => [u.id, u.name])), [userOptions]);

  const answerByFieldId = useMemo(
    () => new Map((checklist?.answers ?? []).map((a) => [a.fieldId, a])),
    [checklist],
  );

  const geoCheck = useMemo(() => {
    if (!checklist) return null;
    const requiresGps = checklist.templateSnapshot.checklistSettings?.captureGps === true;
    if (!requiresGps || !checklist.completedAt) return null;

    const gps = checklist.submissionMeta?.gps;
    if (!gps) return { label: 'Sem GPS', icon: 'alert-triangle' as const };

    const distance = checklist.submissionMeta?.distanceFromUnitMeters;
    if (distance !== undefined && distance > 200) {
      return { label: `⚠ enviado a ${Math.round(distance)}m da unidade`, icon: 'map-pin' as const };
    }
    return null;
  }, [checklist]);

  if (isLoading || !id) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <ListPageLoading message="Carregando checklist..." />
      </div>
    );
  }

  if (error || !checklist) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="text-center">
          <p style={{ color: 'var(--theme-error-color)' }} className="mb-3">
            {(error as Error | undefined)?.message ?? 'Checklist não encontrado'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/checklists')}
            className="text-sm hover:underline"
            style={{ color: 'var(--theme-primary)' }}
            data-testid="checklist-review-error-back"
          >
            Voltar para a lista
          </button>
        </div>
      </div>
    );
  }

  const meta = CHECKLIST_STATUS_META[checklist.status];
  const unitName = checklist.unitId ? unitMap.get(checklist.unitId) : undefined;
  const assigneeName = userMap.get(checklist.assigneeId);
  const isAssignee = !!state.user?.id && checklist.assigneeId === state.user.id;

  const fields = [...checklist.templateSnapshot.fields].sort((a, b) => a.order - b.order);

  const handleApprove = async (): Promise<void> => {
    const ok = await confirm({
      message: 'Aprovar este checklist? O responsável será notificado.',
      title: 'Aprovar checklist',
      confirmButtonText: 'Aprovar',
      variant: 'default',
    });
    if (!ok) return;
    await reviewMutation.mutateAsync({ id: checklist.id, data: { decision: 'approved' } });
  };

  const openReject = (): void => {
    setRejectComment('');
    setRejectError(undefined);
    setRejectOpen(true);
  };

  const submitReject = async (): Promise<void> => {
    if (!rejectComment.trim()) {
      setRejectError('Descreva o motivo da reprovação.');
      return;
    }
    await reviewMutation.mutateAsync({ id: checklist.id, data: { decision: 'rejected', comment: rejectComment.trim() } });
    setRejectOpen(false);
  };

  const handleReopen = async (): Promise<void> => {
    await reopenMutation.mutateAsync(checklist.id);
  };

  const canReviewNow = canReview && checklist.status === 'completed' && !isAssignee;
  const canReopenNow = canReview && (checklist.status === 'rejected' || checklist.status === 'expired');

  const mobileActions: CardAction[] = [
    canReviewNow && { label: 'Aprovar', icon: 'check-circle', onClick: () => void handleApprove() },
    canReviewNow && { label: 'Reprovar', icon: 'x-circle', onClick: openReject, variant: 'danger' },
    canReopenNow && { label: 'Reabrir para correção', icon: 'rotate-ccw', onClick: () => void handleReopen() },
  ].filter(Boolean) as CardAction[];

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="checklist-review-page">
      <PageToolbar
        data-testid="checklist-review-toolbar"
        left={
          <Breadcrumb
            items={[
              { label: 'Checklists', onNavigate: () => navigate('/checklists'), testid: 'checklist-review-breadcrumb-list' },
              { label: checklist.templateSnapshot.name, testid: 'checklist-review-breadcrumb-current' },
            ]}
            mobileLegend="Checklists"
            data-testid="checklist-review-breadcrumb"
          />
        }
        right={
          <>
            <div className="hidden sm:flex items-center gap-2 flex-none">
              {canReviewNow && (
                <>
                  <Button
                    variant="error"
                    icon="x-circle"
                    collapsibleLabel
                    onClick={openReject}
                    loading={reviewMutation.isPending}
                    data-testid="checklist-reject"
                  >
                    Reprovar
                  </Button>
                  <Button
                    variant="success"
                    icon="check-circle"
                    collapsibleLabel
                    onClick={() => void handleApprove()}
                    loading={reviewMutation.isPending}
                    data-testid="checklist-approve"
                  >
                    Aprovar
                  </Button>
                </>
              )}
              {canReopenNow && (
                <Button
                  variant="secondary"
                  icon="rotate-ccw"
                  collapsibleLabel
                  onClick={() => void handleReopen()}
                  loading={reopenMutation.isPending}
                  data-testid="checklist-reopen"
                >
                  Reabrir para correção
                </Button>
              )}
            </div>
            {mobileActions.length > 0 && (
              <div className="sm:hidden flex-none">
                <CardActionsMenu actions={mobileActions} size="sm" />
              </div>
            )}
          </>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {/* Cabeçalho: modelo, status, unidade, responsável, enviado em, geo-check */}
          <div
            className="rounded-xl p-5 mb-4"
            style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
            data-testid="checklist-review-header"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h1 className="text-xl font-bold truncate" style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em' }}>
                  {checklist.templateSnapshot.name}
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                  {unitName ? `${unitName} · ` : ''}Responsável: {assigneeName ?? '—'}
                </p>
              </div>
              <StatusPill variant={meta.variant} label={meta.label} data-testid="checklist-review-status" />
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              <span className="inline-flex items-center gap-1.5">
                <Icon name="clock" className="w-3.5 h-3.5" />
                {checklist.completedAt
                  ? `Enviado em ${format(new Date(checklist.completedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`
                  : formatDueRelative(checklist.expiresAt).label}
              </span>
              {checklist.score !== undefined && (
                <span className="inline-flex items-center gap-1.5">
                  <Icon name="bar-chart" className="w-3.5 h-3.5" />
                  Nota: {Math.round(checklist.score * 100)}%
                </span>
              )}
              {checklist.review?.reviewedAt && (
                <span className="inline-flex items-center gap-1.5">
                  <Icon name={checklist.review.decision === 'approved' ? 'check-circle' : 'x-circle'} className="w-3.5 h-3.5" />
                  Revisado em {format(new Date(checklist.review.reviewedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
              )}
            </div>

            {geoCheck && (
              <div className="mt-3">
                <StatusPill variant="warning" label={geoCheck.label} data-testid="checklist-geo-check" />
              </div>
            )}

            {checklist.review?.comment && (
              <div
                className="mt-4 rounded-lg p-3 text-sm"
                style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}
                data-testid="checklist-review-comment"
              >
                <strong>Motivo da reprovação:</strong> {checklist.review.comment}
              </div>
            )}

            {canReview && checklist.status === 'completed' && isAssignee && (
              <p className="mt-4 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                Você é o responsável por este checklist — outro revisor precisa aprová-lo ou reprová-lo.
              </p>
            )}
          </div>

          {/* Respostas — read-only sobre templateSnapshot.fields, ordem + pareamento por fieldId */}
          <div
            className="rounded-xl p-5"
            style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
            data-testid="checklist-review-fields"
          >
            {fields.map((field) => (
              <AnswerField
                key={field.id}
                field={field}
                answer={answerByFieldId.get(field.id)}
                onOpenPhoto={setLightboxUrl}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Modal Reprovar — comentário obrigatório (tema claro fixo, molde Modal shared) */}
      <Modal isOpen={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="md">
        <ModalHeader title="Reprovar checklist" onClose={() => setRejectOpen(false)} />
        <ModalContent>
          <Textarea
            label="Motivo da reprovação"
            description="Obrigatório — o responsável verá este texto ao reabrir o checklist."
            value={rejectComment}
            onChange={(e) => { setRejectComment(e.target.value); if (rejectError) setRejectError(undefined); }}
            rows={4}
            required
            error={rejectError}
            data-testid="checklist-reject-comment"
          />
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setRejectOpen(false)} data-testid="checklist-reject-cancel">
            Cancelar
          </Button>
          <Button
            variant="error"
            onClick={() => void submitReject()}
            loading={reviewMutation.isPending}
            data-testid="checklist-reject-submit"
          >
            Reprovar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Lightbox simples — overlay de imagem única, fecha por X/ESC/clique fora (view-only) */}
      {lightboxUrl &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-6"
            style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
            onClick={() => setLightboxUrl(null)}
            data-testid="checklist-lightbox"
          >
            <button
              type="button"
              className="absolute top-4 right-4 p-2 rounded-full"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffffff' }}
              onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
              aria-label="Fechar"
              data-testid="checklist-lightbox-close"
            >
              <Icon name="x" className="w-5 h-5" />
            </button>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <img
              src={lightboxUrl}
              alt="Foto enviada no checklist"
              className="max-w-full max-h-full rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

ChecklistReviewPage.displayName = 'ChecklistReviewPage';

/* ------------------------------------------------------------------ */
/* Renderização por campo                                             */
/* ------------------------------------------------------------------ */

const PhotoGridAnswer: React.FC<{ value: FormAnswer['value'] | undefined; onOpen: (url: string) => void }> = ({ value, onOpen }) => {
  const photos = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

  if (photos.length === 0) {
    return <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Nenhuma foto enviada</p>;
  }

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
      {photos.map((url, index) => (
        <button
          key={`${url}-${index}`}
          type="button"
          onClick={() => onOpen(url)}
          className="aspect-square rounded-lg overflow-hidden border"
          style={{ borderColor: 'var(--theme-card-border)' }}
          data-testid={`checklist-photo-${index}`}
        >
          <img src={url} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
};

const ConformityAnswer: React.FC<{ value: FormAnswer['value'] | undefined }> = ({ value }) => {
  const key = typeof value === 'string' ? value : undefined;
  const conformityMeta = key ? CONFORMITY_META[key] : undefined;

  if (!conformityMeta) {
    return <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Não respondido</p>;
  }
  return <StatusPill variant={conformityMeta.variant} label={conformityMeta.label} />;
};

const AnswerField: React.FC<{ field: FormField; answer?: FormAnswer; onOpenPhoto: (url: string) => void }> = ({ field, answer, onOpenPhoto }) => {
  if (field.type === FormFieldType.HEADING) {
    return (
      <h3 className="text-base font-bold pt-3 pb-1 first:pt-0" style={{ color: 'var(--theme-card-text)' }}>
        {field.label}
      </h3>
    );
  }
  if (field.type === FormFieldType.PARAGRAPH) {
    return (
      <p className="text-sm pb-2" style={{ color: 'var(--theme-muted-text)' }}>
        {field.label}
      </p>
    );
  }

  return (
    <div className="py-3 border-t first:border-t-0" style={{ borderColor: 'var(--theme-card-border)' }} data-testid={`checklist-field-${field.id}`}>
      <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>{field.label}</p>
      {field.type === FormFieldType.PHOTO ? (
        <PhotoGridAnswer value={answer?.value} onOpen={onOpenPhoto} />
      ) : field.type === FormFieldType.CONFORMITY ? (
        <ConformityAnswer value={answer?.value} />
      ) : (
        <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>{formatPlainAnswer(answer?.value)}</p>
      )}
    </div>
  );
};
